import { DialogService } from '../../services/dialog/dialog.service';
import { SnackbarService } from '../../services/snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { KeyValue } from './key-value-table.component';
import { InputType } from '../../services/dialog/components/input-dialog/input-dialog.component';

export interface CreateUpdateKeyValuePairOptions {
  key: string;
  updateValueFn: (value: string) => void;
  getValueFn: () => string;
  acceptInputFn?: (input: string) => boolean;
  errorMessage?: string;
  type?: InputType;
  cellClickFn?: () => void;
  valuePrefix?: string;
  formatter?: (value: string) => string;
}

export class KeyValueTableService {
  private translateService: TranslateService;
  private dialogService: DialogService;
  private snackbarService: SnackbarService;

  private constructor(
    translateService: TranslateService,
    dialogService: DialogService,
    snackbarService: SnackbarService,
  ) {
    this.translateService = translateService;
    this.dialogService = dialogService;
    this.snackbarService = snackbarService;
  }

  public static create(
    translateService: TranslateService,
    dialogService: DialogService,
    snackbarService: SnackbarService,
    afterUpdatedFunction?: (value: string) => void,
  ): KeyValueTableService {
    const keyValueTableService = new KeyValueTableService(
      translateService,
      dialogService,
      snackbarService,
    );
    if (afterUpdatedFunction) {
      keyValueTableService.addAfterUpdatedFunction(afterUpdatedFunction);
    }
    return keyValueTableService;
  }

  afterUpdatedFunction: ((value: string) => void) | undefined;
  public addAfterUpdatedFunction(func: (value: string) => void) {
    this.afterUpdatedFunction = func;
  }

  public createKeyValuePair(key: string, value: string): KeyValue {
    return {
      key: key,
      value: value,
    };
  }

  public createUpdateKeyValuePair(
    options: CreateUpdateKeyValuePairOptions,
  ): KeyValue {
    const {
      key,
      updateValueFn,
      getValueFn,
      acceptInputFn = () => true,
      errorMessage = '',
      type = 'text',
      cellClickFn,
      valuePrefix,
      formatter,
    } = options;

    const keyValuePair: KeyValue = {
      key: key,
      value: getValueFn(),
      cellClick: cellClickFn,
      valuePrefix: valuePrefix,
      tryUpdate: () => {
        const afterClose = (value: string) => {
          updateValueFn(value);
          if (this.afterUpdatedFunction) {
            this.afterUpdatedFunction(value);
          }
          keyValuePair.value = getValueFn();
          this.snackbarService.show('components.input-dialog.snackbar');
        };

        this.dialogService.openInputDialog({
          inputCheckerFn: acceptInputFn,
          afterCloseFn: afterClose,
          inputLabel: key,
          text: 'components.detail-information.update',
          errorMessage,
          defaultInput: getValueFn(),
          type,
          formatterFn: formatter,
        });
      },
    };

    return keyValuePair;
  }

  createKeyTranslateValuePair(key: string, value: string): KeyValue {
    return {
      key: key,
      value: value,
      translateValue: true,
    };
  }
}
